import {
  getCurrentPosition,
  isGeolocationAvailable,
  checkLocationPermission,
} from '../../location/getLocation'
import { nativeAdapterUnavailable } from './unavailable'
import { isNativePlatform } from '../runtime'
import {
  fetchLocationChallenge,
  submitLocationChallengeSample,
  reportLocationChallengeUnavailable,
} from '../backgroundLocationChallenge'
import { NativeLocationChallenge } from './nativeLocationChallenge'
import { NATIVE_ERROR_CODES } from '../nativeErrorCodes'

/**
 * Foreground location (#1690, addendum §5.2 row 2). Both Web and Native use
 * `navigator.geolocation` today — spec.md §2.2 explicitly allows native to
 * reuse the Web API "אם הוא עבר בדיקות" instead of a dedicated plugin, and
 * #1689's ios/android WebViews expose the standard Geolocation API. This
 * function is the single seam a later ticket swaps for a native-specific
 * plugin if real-device testing finds the Web API insufficient — no new
 * caller should reach for `navigator.geolocation` directly.
 */
export function getForegroundLocationAdapter() {
  return { getCurrentPosition, isGeolocationAvailable, checkLocationPermission }
}

// Mirrors `LOCATION_UNAVAILABLE_REASONS` in
// `functions/nativeApp/locationChallengeModel.js` — the WIRE VALUES of that
// server-side enum, not its code. There is no shared-import path from
// `src/` into `functions/` (separate packages), so only the subset a client
// can ever legitimately report from a geolocation failure is duplicated
// here; keep these string literals in sync with that file by hand if it
// changes.
const LOCATION_UNAVAILABLE_REASONS = Object.freeze({
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  GPS_DISABLED: 'GPS_DISABLED',
  TIMEOUT: 'TIMEOUT',
  UNKNOWN: 'UNKNOWN',
})

/**
 * Takes exactly ONE raw GPS fix for the Location Challenge flow (spec.md
 * §8.3 items 5-6) — deliberately NOT `getCurrentPosition` above: that
 * helper converts a `GeolocationPositionError` into a Hebrew-message-only
 * `Error`, which loses the `.code` this flow needs to report a specific
 * `LOCATION_UNAVAILABLE_REASONS` value (§9.1) rather than a generic one.
 * Nobody sees this flow's errors in the UI (it runs from a silent push,
 * §8.3), so there is no Hebrew-message requirement to preserve here.
 *
 * @return {Promise<{latitude:number, longitude:number, accuracy:number, timestamp:number}>}
 */
function takeRawPositionSample() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject({ code: 'UNSUPPORTED' })
      return
    }
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp,
      }),
      (error) => reject(error),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    )
  })
}

/**
 * @param {*} error A `GeolocationPositionError`, or the `{code:'UNSUPPORTED'}`
 *   sentinel `takeRawPositionSample` rejects with when `navigator.geolocation`
 *   itself is missing.
 * @return {string} One of LOCATION_UNAVAILABLE_REASONS.
 */
function mapGeolocationErrorToReason(error) {
  switch (error?.code) {
    case error?.PERMISSION_DENIED:
      return LOCATION_UNAVAILABLE_REASONS.PERMISSION_DENIED
    case error?.POSITION_UNAVAILABLE:
      return LOCATION_UNAVAILABLE_REASONS.GPS_DISABLED
    case error?.TIMEOUT:
      return LOCATION_UNAVAILABLE_REASONS.TIMEOUT
    default:
      return LOCATION_UNAVAILABLE_REASONS.UNKNOWN
  }
}

function newIdempotencyKey() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * The one place a received silent push (`{challengeId}` — the native
 * plugin listener that delivers it here is #1696 task 9, not this function)
 * turns into a sample: fetch the challenge's own metadata (§10.2, never the
 * vehicle location — see `getLocationChallenge.js`'s header), take one GPS
 * fix, and submit it. A failure at any step reports
 * `submitLocationUnavailable` with the most specific reason available
 * rather than leaving the challenge PENDING forever — an unanswered
 * challenge has no separate EXPIRED state (see
 * `locationChallengeModel.js`'s own comment on why), it simply never gets
 * evidence.
 *
 * Expiry is checked before sampling (#2170), from the server's own
 * `expired` verdict — not by re-deriving it from `expiresAt` on the device
 * clock. The earlier version relied on `submitLocationSample`'s server-side
 * staleness check alone (§8.4's `stalenessSeconds` in
 * `decideValidationResult`), but that runs only AFTER the fix was taken and
 * sent, and the coordinates are stored even when the answer is
 * `LOCATION_UNAVAILABLE`/`STALE_SAMPLE`. That check still stands as the
 * second line for a sample that is taken in time and arrives late.
 *
 * @param {object} params Parameters.
 * @param {string} params.challengeId
 * @return {Promise<{status:string, reason?:string, result?:?string}>}
 */
async function requestBackgroundLocationSample({ challengeId }) {
  let challenge
  try {
    challenge = await fetchLocationChallenge(challengeId)
  } catch (error) {
    // The challenge itself could not be confirmed (not-found, wrong device,
    // malformed id) — nothing to sample against. A typed result, not a
    // throw, keeps every caller (the native push listener) on one
    // non-throwing contract.
    return { status: 'challenge-unavailable', error: String(error?.message || error) }
  }

  if (!challenge || challenge.status === 'CONSUMED') {
    // Already answered — a replay of the same push, or a race with a
    // second delivery. Nothing left to sample or submit.
    return { status: 'already-consumed' }
  }

  // #2170 (spec 2110 §31 P04, §6.3): a challenge whose window already closed
  // is never sampled. The server decides `expired` on its own clock;
  // `=== true` means an older server that does not send the field keeps the
  // previous behaviour instead of silently stopping every sample.
  if (challenge.expired === true) {
    const reason = 'STALE_SAMPLE'
    let outcome = null
    try {
      outcome = await reportLocationChallengeUnavailable({ challengeId, reason })
    } catch (error) {
      // Nothing was collected either way; a failed report leaves the
      // challenge PENDING, which is harmless (see locationChallengeModel.js).
    }
    // errorCode (#2384, spec §12.1): "האתגר פג ואינו יכול להפוך ל-MATCH" —
    // additive; `reason` stays the §9.1 wire vocabulary the server stores.
    return {
      status: 'expired',
      reason,
      result: outcome?.result ?? null,
      errorCode: NATIVE_ERROR_CODES.CHALLENGE_EXPIRED,
    }
  }

  let position
  try {
    position = await takeRawPositionSample()
  } catch (error) {
    const reason = mapGeolocationErrorToReason(error)
    const outcome = await reportLocationChallengeUnavailable({ challengeId, reason })
    return { status: 'unavailable', reason, result: outcome?.result ?? null }
  }

  const outcome = await submitLocationChallengeSample({
    challengeId,
    sampledAt: new Date(position.timestamp ?? Date.now()).toISOString(),
    lat: position.latitude,
    lng: position.longitude,
    accuracy: position.accuracy,
    idempotencyKey: newIdempotencyKey(),
  })

  return { status: 'submitted', result: outcome?.result ?? null, reason: outcome?.reason ?? null }
}

/**
 * Background location — the driver Location Challenge flow (spec.md §8).
 * Unsupported on Web/PWA by design (§2.2) — always UNAVAILABLE there, never
 * a silent no-op that could look like a missed challenge instead of an
 * unbuilt/unsupported one. On native (#1696), `requestSample` runs the real
 * fetch/sample/submit flow above.
 */
export function getBackgroundLocationAdapter() {
  if (!isNativePlatform()) {
    return {
      requestSample: () => nativeAdapterUnavailable('location.backgroundSample'),
    }
  }
  return { requestSample: requestBackgroundLocationSample }
}

/**
 * Runs `requestBackgroundLocationSample` for one received/pending challenge,
 * then always releases it via `completeChallenge()` — including on failure,
 * since `requestBackgroundLocationSample` itself already reports a failure
 * to the server (via `submitLocationUnavailable`) rather than throwing in
 * the normal case; this still guards the abnormal case (e.g. the plugin
 * bridge itself rejects) so a single bad delivery can't leave iOS's
 * `fetchCompletionHandler` open past its budget (see the native plugin's own
 * comment on that budget). A no-op — and no `completeChallenge()` call — when
 * `data` carries no challenge at all (e.g. `getPendingChallenge()` finding
 * nothing at cold start): there is no native handler waiting to be released.
 *
 * @param {?{challengeId: string}} data
 */
async function handleReceivedChallenge(data) {
  if (!data?.challengeId) return
  try {
    await requestBackgroundLocationSample({ challengeId: data.challengeId })
  } finally {
    NativeLocationChallenge.completeChallenge().catch(() => {})
  }
}

let challengeListenerSubscribed = false

/**
 * Arms the Background Location Challenge silent-push listener (#1696 task
 * 9). Must run at app startup regardless of login state — unlike
 * `registerThisDevice` (tied to the OTP-login moment, see
 * nativeDeviceRegistry.js), a silent push can arrive whenever the app
 * process is alive, and #8's `src/index.js` has no other native-bootstrap
 * hook this belongs to.
 *
 * Two sources, both funnelled through the same `handleReceivedChallenge`:
 *  - `getPendingChallenge()` once, for cold start (the push already arrived
 *    and woke the process before any JS listener could attach — same
 *    pattern as `NativePushPlugin.getLastTappedPush`).
 *  - the `'locationChallengeReceived'` event, for every later delivery.
 *
 * No-op on Web (§2.2 — background location is native-only) and idempotent,
 * matching `push.js`'s own `rotationSubscribed` module-level-flag pattern
 * so a caller re-invoking this (e.g. a remount) never double-subscribes.
 */
export function initBackgroundLocationChallengeListener() {
  if (!isNativePlatform() || challengeListenerSubscribed) return
  challengeListenerSubscribed = true

  NativeLocationChallenge.getPendingChallenge()
    .then((result) => handleReceivedChallenge(result?.data))
    .catch(() => {})

  NativeLocationChallenge.addListener('locationChallengeReceived', (data) => (
    handleReceivedChallenge(data)
  )).catch(() => {})
}
